import React, { useLayoutEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import isEqual from 'lodash/isEqual'

import {
  fullHeightContainer,
  removeFullHeightContainer,
} from '../../../utilities/helpers'

import { useModal } from '../../../providers/ModalProvider/ModalProvider'

import { Navigation } from './Navigation'
import { PackageMap } from './PackageMap'
import { SelectGASeats } from '../../ModalContent'

const normalizeSeatId = (seatId) => String(seatId ?? '').trim()

const getSeatMapValue = (seatMap, seatId) => {
  if (!seatMap) return undefined
  const normalizedSeatId = normalizeSeatId(seatId)
  return seatMap.get(seatId) || seatMap.get(normalizedSeatId)
}

export default function ConfigureMap({
  eventPackage,
  isMapDisabled,
  isPackagePublished,
  hasPackageEnded,
  selectedSeatIds,
  savePackageInventory,
  isSaving,
  data,
  background,
  seatsInPackage,
  setSelectedSeatIds,
  isAssigningToFan,
  soldMap,
  reservedMap,
  holdMap,
  initialState,
  packageSalesBySection,
  highestAttendanceBySection,
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const { openModal } = useModal()

  const handleGoBack = () => {
    navigate('..')
  }

  const checkDisabled = () => {
    return isEqual(initialState, selectedSeatIds)
  }

  const getGaPackageSeatIds = (sectionId, matchesTicket) => {
    const normalizedSectionId = normalizeSeatId(sectionId)
    if (!normalizedSectionId) return []

    const seatIds = []
    for (const ticket of eventPackage?.package_tickets || []) {
      const isGa = Boolean(ticket?.GA ?? ticket?.ga)
      const ticketSectionId = normalizeSeatId(
        ticket?.sectionId ?? ticket?.section_id
      )
      const seatId = normalizeSeatId(ticket?.seatId ?? ticket?.seat_id)
      if (
        !isGa ||
        !seatId ||
        ticketSectionId !== normalizedSectionId ||
        !matchesTicket(ticket)
      ) {
        continue
      }
      seatIds.push(seatId)
    }
    return [...new Set(seatIds)]
  }

  const getAvailableGaPackageSeatIds = (sectionId) =>
    getGaPackageSeatIds(
      sectionId,
      (ticket) => ticket?.on_sale_status === 'available'
    )

  // Anything a fan already holds. These can't be removed from the package, so
  // they have to survive every inventory edit.
  const getCommittedGaPackageSeatIds = (sectionId) =>
    getGaPackageSeatIds(
      sectionId,
      (ticket) => ticket?.on_sale_status !== 'available'
    )

  const openGAModal = (section) => {
    const isSeatOnHold = (seatId) => Boolean(getSeatMapValue(holdMap, seatId))
    const isSeatSoldOrReserved = (seatId) =>
      Boolean(getSeatMapValue(soldMap, seatId)) ||
      Boolean(getSeatMapValue(reservedMap, seatId))

    let seatsToAssignFrom = []
    if (isAssigningToFan) {
      const totalCapacity = section.spots?.length || 0
      // Package ticket on_sale_status is the source of truth for assignment.
      // purchase_log sold counts can lag or go negative after releases.
      // Prefer non-held seats first so small quantities don't consume holds.
      const availablePackageSeatIds = getAvailableGaPackageSeatIds(
        section.sectionId
      ).filter((seatId) => !isSeatSoldOrReserved(seatId))
      const nonHeldSeatIds = availablePackageSeatIds.filter(
        (seatId) => !isSeatOnHold(seatId)
      )
      const heldSeatIds = availablePackageSeatIds.filter((seatId) =>
        isSeatOnHold(seatId)
      )
      const orderedSeatIds = [...nonHeldSeatIds, ...heldSeatIds]
      const highestIndividualGameAttendance =
        highestAttendanceBySection[section.sectionId] || 0
      const availableSeatsInSection = Math.max(
        0,
        totalCapacity - highestIndividualGameAttendance
      )
      const availableSpots = Math.min(
        orderedSeatIds.length,
        availableSeatsInSection
      )

      seatsToAssignFrom = orderedSeatIds.slice(0, Math.max(availableSpots, 0))
    } else {
      seatsToAssignFrom = (section?.spots || []).filter(
        (seatId) => !isSeatOnHold(seatId)
      )
    }

    openModal(
      SelectGASeats,
      {
        setSelectedSeatIds: setSelectedSeatIds,
        section: section,
        seatsToAssignFrom: seatsToAssignFrom,
        holdMap,
        mode: isAssigningToFan ? 'assign' : 'inventory',
        currentSeatIds: selectedSeatIds?.gaSeats?.[section?.sectionId] || [],
        committedSeatIds: isAssigningToFan
          ? []
          : getCommittedGaPackageSeatIds(section?.sectionId),
      },
      { className: '', contentClassName: '' }
    )
  }

  useLayoutEffect(() => {
    ;['html', 'body'].forEach((el) =>
      document.querySelector(el).classList.add('full-height')
    )

    return () => {
      ;['html', 'body'].forEach((el) =>
        document.querySelector(el).classList.remove('full-height')
      )
    }
  }, [location.pathname])

  useLayoutEffect(() => {
    // if is not assigning to fan, hide navbar, banner, and sidebar
    // already hidden in PackageSeatmapWrapper.js if is assigning to fan
    if (!isAssigningToFan) {
      const nav = document.getElementById('navbar')
      nav.style.display = 'none'
      const banner = document.getElementById('banner')
      banner.style.display = 'none'
      const sidebarNav = document.getElementById('sidebarMenu')
      sidebarNav.style.display = 'none'

      const container = document.getElementById('main-container')
      fullHeightContainer(container)
      container.classList.remove('sidebar-container')

      const content = document.getElementById('main-content')
      content.classList.add('pb-0')
      content.classList.add('full-height-wrapper')
      content.classList.remove('spacer-md', 'spacer-md--with-banner')

      return () => {
        nav.style.display = ''
        banner.style.display = ''
        sidebarNav.style.display = ''
        removeFullHeightContainer(container)
        content.classList.remove('pb-0')
        content.classList.remove('full-height-wrapper')
        content.classList.add('spacer-md', 'spacer-md--with-banner')
        container.classList.add('sidebar-container')
      }
    }
  }, [location.pathname, isAssigningToFan])

  return (
    <div data-testid="package-seatmap-wrapper-section" aria-busy={isSaving}>
      <Navigation
        eventPackage={eventPackage}
        isMapDisabled={isMapDisabled}
        isPackagePublished={isPackagePublished}
        hasPackageEnded={hasPackageEnded}
        selectedSeatIds={selectedSeatIds}
        savePackageInventory={savePackageInventory}
        handleGoBack={handleGoBack}
        isDisabled={checkDisabled()}
        isSaving={isSaving}
        data={data}
      />
      <div
        className="package-seatmap-stage"
        {...(isSaving ? { inert: '' } : {})}
        aria-hidden={isSaving || undefined}
      >
        {data && background && (
          <PackageMap
            data={data}
            background={background}
            seatsInPackage={seatsInPackage}
            selectedSeatIds={selectedSeatIds}
            setSelectedSeatIds={setSelectedSeatIds}
            isMapDisabled={isMapDisabled}
            isAssigningToFan={isAssigningToFan}
            soldMap={soldMap}
            reservedMap={reservedMap}
            holdMap={holdMap}
            openGAModal={openGAModal}
            eventPackage={eventPackage}
          />
        )}
      </div>
    </div>
  )
}
